/* The page's card session with Pocket Factory's AI assistant (Text / Call: assets/pocket-textcall.js).
   The card on the page goes to the Worker (POST /v1/call/session) for a session token and a tel: link that carries a
   hidden dial token (tel:+18882189635,,DIGITS: the phone sends the digits while the assistant's greeting plays, and the
   assistant links the call to this card without a word). The shopper never sees, hears or types a code: a desktop shows
   only the number, and its Call tap is recorded, so a call dialed by hand finds this card too (the assistant asks "Are
   you looking at the 2 Matte Booking Cards for Zonova?", or finds it by the business name they say). The session is made
   quietly once the page shows a card preview (and on the first chat message or Call tap), so a call dialed by hand can
   find it. The website chat uses the same session (window.PocketCall).
   What a call, the chat or an email reply changes reaches the page live: a WebSocket (GET /v1/call/session/live, the
   token in its subprotocol, never in a URL) pushes the card as the assistant left it, and while the socket is down the
   page asks GET /v1/call/session/state every second during a call, an open chat or a ringing Call me now (every 5 s
   otherwise, never while the tab is hidden). Each change goes through the page's own controls (the same change/input
   events a tap makes, the website through the page's own lookup), so the preview, price and Buy now label follow, and
   Buy now waits until the preview has redrawn it. The page's own changes go back to the Worker. Without the Worker (or
   CALL_ASSIST off) Call still dials the number. "Call me now" (in the chat, or ?callme=1 on any page:
   snippets/pocket-callme.liquid, shown by assets/pocket-textcall.js) asks for a U.S. number and an explicit consent
   tick, then the Worker calls them (POST /v1/call/outbound), already linked to this card. After 9 PM their time it
   offers 8 AM instead. */
(() => {
  const bar = document.querySelector('[data-buy-bar]');
  const view = document.querySelector('[data-pf-callme]');
  const form = bar?.closest('form') || document;
  const pill = document.querySelector('[data-pf-call-pill]');
  const PHONE = '+18882189635', DISPLAY = '(888) 218-9635';
  const KEY = 'pf.call.v1', AUTO_KEY = 'pf.call.auto';
  /** Polls while the live socket is down: a call, an open chat or a ringing Call me now; else; and a keep-alive. */
  const FAST_MS = 1000, SLOW_MS = 5000, KEEPALIVE_MS = 4 * 60 * 1000;
  /** The page stays in sync this long after the last sign of the shopper (a chat, a Call tap, a change, a tap or key). */
  const WATCH_MS = 30 * 60 * 1000;
  const PING_MS = 30000, BACKOFF_MS = [1000, 2000, 5000, 15000], PURPOSES = ['Reviews', 'Contact', 'Booking', 'Social', 'Website', 'Other'];
  const capture = (name, props) => { try { window.posthog?.capture(name, { flow: document.documentElement.dataset.pfFlow || 'classic', ...props }); } catch (_) {} };
  const phoneLike = () => !!window.matchMedia?.('(pointer: coarse)').matches;
  const device = () => window.matchMedia?.('(max-width: 749px)').matches ? 'phone' : window.matchMedia?.('(max-width: 989px)').matches ? 'tablet' : 'desktop';
  const worker = () => { try { return window.PocketCore?.workerBase?.() || ''; } catch (_) { return ''; } };
  const checked = field => form.querySelector(`[data-card-field="${field}"]:checked`)?.value || null;
  const value = selector => form.querySelector(selector)?.value || null;
  const packSelect = () => bar?.querySelector('select[data-pack]') || form.querySelector('select[data-pack]');
  const purposeNow = () => window.PocketPurpose.get() || null;
  const hostOf = url => { try { return new URL(/^https?:\/\//i.test(url) ? url : 'https://' + url).hostname.replace(/^www\./, '').toLowerCase(); } catch (_) { return ''; } };
  const sameLink = (a, b) => String(a || '').trim().replace(/\/+$/, '').toLowerCase() === String(b || '').trim().replace(/\/+$/, '').toLowerCase();
  let variants = [];
  try { variants = JSON.parse(form.querySelector('[data-variant-map]')?.textContent || '[]') || []; } catch (_) {}
  // In the card color chosen too, where the product offers one (snippets/pf-card-color.liquid).
  const colorAt = () => Number(form.querySelector('[data-pf-cc]')?.dataset.pfCcOption) || 0;
  const priceOf = (pack, finish) => {
    const at = colorAt(), color = at ? (form.querySelector('[data-pf-cc-value]:checked') || form.querySelector('[data-pf-cc-value]'))?.value || '' : '';
    const v = variants.find(item => parseInt(item.option1 || item.title, 10) === pack && (item.option2 || String(item.title).split('/')[1]?.trim() || 'Matte') === finish && (!at || String(item['option' + at] ?? '') === color));
    return v ? v.price / 100 : null;
  };

  /** The card as the page shows it (the fields the Worker keeps; never a price it would trust, never contact details). */
  function snapshot() {
    let distinctId = null;
    try { distinctId = window.posthog?.get_distinct_id?.() || null; } catch (_) {}
    const handle = /\/products\/([a-z0-9-]+)/.exec(location.pathname)?.[1] || 'pocketfactory-card';
    return {
      pack: Number(packSelect()?.value) || null,
      finish: checked('finish'),
      orientation: checked('orientation'),
      front: checked('logoMode'),
      base: checked('baseColor'),
      // No name on the card to read or set: a card never prints one (NAMEFIELD-1).
      businessName: value('[data-order-business]'),
      purpose: purposeNow(),
      // The link a tap opens and the business website, as the order would carry them.
      link: value('[data-order-destination]'),
      website: value('[data-order-website]'),
      handle,
      designKey: value('[data-order-design-key]'),
      previewId: value('[data-order-design]'),
      logoAsset: value('[data-order-logo]'),
      wordmarkAsset: value('[data-order-wordmark]'),
      artworkSource: value('[data-order-artwork-source]'),
      distinctId,
      discountCode: window.PocketOffer?.code?.() || null,
      ...readiness(),
    };
  }

  let session = null, lastRev = 0, lastNudge = 0, applying = false, pushTimer = 0, redrawing = false, nudgeWaiting = 0;
  let onCall = false, chatOpen = false, chatHeld = false, ringing = false, website = null;

  /** The page's own Buy now state for the Worker: on (ready), what holds it (pocket.js bar.dataset.hold), and whether the
   * preview shows the card as it is now (rendered: no call, chat or email edit waiting to be drawn, no website being
   * looked up, no artwork being prepared). The assistant's Buy now nudge waits for a preview drawn for the latest
   * revision. */
  function readiness() {
    const hold = bar?.dataset.hold || '';
    return { ready: !!bar && ['ready', 'sending'].includes(bar.dataset.state) && !hold, hold: hold || null, rendered: !!bar && hold !== 'preview' && !redrawing && !website };
  }
  try { const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null'); if (saved && /^pfsess_[0-9a-f]{64}$/.test(saved.token || '')) session = { token: saved.token, until: Number(saved.until) || 0, dial: saved.dial }; } catch (_) {}

  function keep() { try { session ? sessionStorage.setItem(KEY, JSON.stringify(session)) : sessionStorage.removeItem(KEY); } catch (_) {} }
  /** The number, or the session's tel: link (two pauses, then its hidden dial token; never a trailing #). */
  const dialOf = link => /^tel:\+1\d{10},,\d{6}#?$/.test(link || '') ? link.replace(/#$/, '') : 'tel:' + PHONE;
  /** A Worker path for this session: the session rides only in the Authorization header (kept for callers of 1.22.0). */
  const at = path => path;

  async function api(path, options = {}) {
    const base = worker();
    if (!base) throw new Error('no worker');
    const { timeout = 6000, ...init } = options;
    const headers = { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...(session?.token ? { Authorization: 'Bearer ' + session.token } : {}), ...init.headers };
    const response = await fetch(base + path, { ...init, headers, credentials: 'omit', signal: AbortSignal.timeout?.(timeout) });
    if (!response.ok) throw Object.assign(new Error('call ' + response.status), { status: response.status, body: await response.json().catch(() => null) });
    return response.status === 204 ? null : response.json();
  }

  /** This visit's card session (made once: when the preview shows, or on the first chat message or call). */
  let making = null;
  async function ensureSession() {
    if (session) { touch(); return session; }
    making ||= (async () => {
      const card = bar ? snapshot() : {};
      let sid = '';
      try { sid = window.PocketCore?.sessionId?.() || ''; } catch (_) {}
      const made = await api('/v1/call/session', { method: 'POST', body: JSON.stringify({ config: card, sessionId: sid }), headers: sid ? { 'X-PF-Session': sid } : {} });
      if (!/^pfsess_[0-9a-f]{64}$/.test(made?.token || '')) throw new Error('no session');
      if (bar) lastReport = JSON.stringify([readinessReport(card), 1]);
      session = { token: made.token, until: Date.now() + WATCH_MS, dial: dialOf(made.dial) };
      lastRev = 1; lastNudge = 0; keep();
      watch();
      return session;
    })().finally(() => { making = null; });
    return making;
  }

  /** Call: the same assistant by phone, with this card and conversation. A phone dials the session's tel: link (the
   * card links itself); a desktop shows the number. The tap is recorded either way, so a call dialed by hand in the
   * next 2 minutes is asked about this card. Returns what to show: never a code. */
  async function callAgent() {
    const card = bar ? snapshot() : {};
    capture('pf_call_click', { pack: card.pack ?? null, finish: card.finish ?? null, price: card.pack && card.finish ? priceOf(card.pack, card.finish) : null, device: device() });
    const phone = phoneLike();
    let dial = 'tel:' + PHONE, linked = false;
    try {
      const made = await ensureSession();
      dial = dialOf(made.dial);
      linked = true;
      mark('call_tap');
    } catch (_) {}
    const text = phone ? `Calling ${DISPLAY}…` : `Call ${DISPLAY} free from your phone.${linked ? ' The assistant will pull up this card with you.' : ''}`;
    if (phone) setTimeout(() => { location.href = dial; }, 400);
    return { text, dial, phone };
  }

  /** A moment the call log and the assistant use: call_tap (they tapped Call), checkout_started. Never waits. */
  function mark(type) {
    const base = worker();
    if (!session || !base) return;
    try { fetch(base + '/v1/call/session/event', { method: 'POST', keepalive: true, credentials: 'omit', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.token }, body: JSON.stringify({ type }) }).catch(() => {}); } catch (_) {}
  }

  // The card's purpose and the link a tap opens, set the way a person sets them: the purpose's own radio, then that
  // purpose's link field (opened if it is folded away). A review link also picks its platform. The purpose is the
  // shopper's, marked pfChosen as pocket-design.js marks a chip: question 2 follows it, a lookup that lands later never
  // replaces it, and the order line says it was chosen (_Purpose source: manual).
  function setPurpose(want, source) {
    if (!PURPOSES.includes(want) || purposeNow() === want) return false;
    return window.PocketPurpose.set(want, { chosen: source || 'call' });
  }
  const PLATFORMS = [['Google', /(^|\.)(google\.[a-z.]+|g\.page|goo\.gl)$/], ['Yelp', /(^|\.)yelp\.[a-z.]+$/], ['Trustpilot', /(^|\.)trustpilot\.com$/]];
  function setLink(raw) {
    let url = '';
    try { url = window.PocketBrandService?.normalizeDestination?.(String(raw)) || ''; } catch (_) { url = ''; }
    if (!url && /^https?:\/\//i.test(String(raw || ''))) url = String(raw).trim();
    if (!/^https?:\/\//i.test(url)) return false;
    const p = purposeNow() || 'Reviews';
    if (p === 'Reviews') {
      const platform = PLATFORMS.find(([, re]) => re.test(hostOf(url)))?.[0] || 'Other';
      const radio = form.querySelector(`[name="properties[Review platform]"][value="${platform}"]`);
      if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    const input = p === 'Reviews' ? form.querySelector('[data-rv-review-link]') : form.querySelector(`[data-purpose-fields="${p}"] [data-destination-input]`);
    if (!input || sameLink(input.value, url)) return false;
    const details = input.closest('details');
    if (details && !details.open) details.open = true;
    input.disabled = false;
    input.value = url;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /** Their business website, through the page's own lookup: the homepage's website field (the same form a person
   * submits), else the page's lookup and its brand store. Starts it and returns true; the card redraws when it lands. */
  function setWebsite(raw) {
    const service = window.PocketBrandService;
    let url;
    try { url = service.normalizeWebsite(String(raw)); } catch (_) { return false; }
    const host = hostOf(url);
    if (!host || host === hostOf(value('[data-order-website]') || '') || host === website?.host) return false;
    website = { host, startedAt: Date.now() };
    const done = () => { if (website?.host !== host) return; website = null; settle(); };
    const store = window.PocketPersonalization?.store;
    const landed = () => hostOf(store?.getState?.().brand?.websiteUrl || store?.getState?.().brand?.domain || value('[data-order-website]') || '') === host;
    const unwatch = store?.subscribe?.(() => { if (landed()) { unwatch?.(); setTimeout(done, 0); } });
    setTimeout(() => { unwatch?.(); done(); }, 20000);
    const heroForm = document.querySelector('[data-rv-url-form]');
    const typed = heroForm?.querySelector('.pfc-input'), carrier = heroForm?.querySelector('[data-rv-url]');
    if (heroForm && carrier && heroForm.getClientRects().length && !heroForm.closest('[hidden]')) {
      const field = typed || carrier;
      field.value = url.replace(/^https:\/\//, '');
      field.dispatchEvent(new Event('input', { bubbles: true }));
      if (heroForm.requestSubmit) heroForm.requestSubmit(); else heroForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      return true;
    }
    if (!store?.setBrand || !service?.createResolver) { website = null; return false; }
    service.createResolver()(url).then(brand => {
      if (website?.host !== host) return;
      if (brand && (brand.businessName || brand.logoMarkUrl || brand.wordmarkUrl || brand.combinedLogoUrl)) store.setBrand(brand); else done();
    }).catch(done);
    return true;
  }

  /** A website lookup the assistant started has landed (or given up): the rest of its card applies, then a redraw. */
  let after = null;
  function settle() {
    const card = after; after = null;
    const changed = card ? apply(card) : [];
    if (changed.length) document.dispatchEvent(new CustomEvent('pocket:remote-applied', { detail: { fields: changed, source: card.source || 'call', card } }));
    else { redrawing = true; document.dispatchEvent(new CustomEvent('pocket:remote-edit', { detail: { fields: ['website'] } })); }
  }

  /** Applies the call's (chat's, email's) card through the page's own controls; returns the fields that changed. Buy now
   * and quick pay wait until the preview has drawn them (pocket.js: pocket:remote-edit, then pocket:remote-rendered).
   * A new website comes first: the page looks it up and starts a fresh card, and the rest applies once it lands. */
  function apply(card, source) {
    const changed = [];
    applying = true;
    try {
      if (card.website && !website && setWebsite(card.website)) {
        const { website: _site, ...rest } = card;
        after = { ...rest, source };
        changed.push('website');
        return changed;
      }
      if (card.purpose && setPurpose(card.purpose, source || card.source)) changed.push('purpose');
      if (card.link && setLink(card.link)) changed.push('link');
      const select = packSelect();
      if (card.pack && select && select.value !== String(card.pack) && [...select.options].some(o => o.value === String(card.pack))) {
        select.value = String(card.pack); select.dispatchEvent(new Event('change', { bubbles: true })); changed.push('pack');
      }
      for (const [field, key] of [['finish', 'finish'], ['orientation', 'orientation'], ['logoMode', 'front'], ['baseColor', 'base']]) {
        const want = card[key];
        if (!want || checked(field) === want) continue;
        const radio = form.querySelector(`[data-card-field="${field}"][value="${want}"]`);
        if (!radio || radio.disabled) continue;
        radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); changed.push(key);
      }
    } finally { applying = false; }
    if (changed.length) { redrawing = true; document.dispatchEvent(new CustomEvent('pocket:remote-edit', { detail: { fields: changed } })); }
    return changed;
  }

  /** The chat's turn changed the card (assets/pocket-textcall.js): drawn on the page like a call's edit. */
  function applyFromChat(card, rev) {
    const changed = card ? apply(card, 'chat') : [];
    if (rev) lastRev = Math.max(lastRev, rev);
    if (card && !changed.length) reportSoon(true);
    if (changed.length) document.dispatchEvent(new CustomEvent('pocket:remote-applied', { detail: { fields: changed, source: 'chat', card } }));
    return changed;
  }

  /** The assistant's nudge only highlights Buy now; it never turns it on. While the bar waits (a redraw, a hold) the
   * highlight waits too, for up to 30 s. */
  function nudgeBuyNow() {
    if (!bar) return;
    const target = bar.querySelector('[data-pf-buy-now]:not([hidden])') || bar.querySelector('[data-purchase]:not([hidden])');
    if (!target || target.disabled || bar.dataset.hold || bar.dataset.state !== 'ready') { nudgeWaiting = Date.now(); return; }
    nudgeWaiting = 0;
    target.classList.remove('pfx-call-nudge'); void target.offsetWidth; target.classList.add('pfx-call-nudge');
    setTimeout(() => target.classList.remove('pfx-call-nudge'), 6000);
  }

  /** Readiness goes to the Worker when it changes, and after each call, chat or email edit is drawn (seen: the last
   * rev this page has drawn). Only these fields: the card itself goes with the shopper's own changes (pushSoon). */
  let lastReport = '', reportTimer = 0;
  const readinessReport = card => ({ ready: card.ready, hold: card.hold, rendered: card.rendered, previewId: card.previewId, designKey: card.designKey });
  function reportSoon(force) {
    if (!session) return;
    clearTimeout(reportTimer);
    reportTimer = setTimeout(async () => {
      if (!session || applying) return;
      const state = readinessReport(snapshot()), key = JSON.stringify([state, lastRev]);
      if (!force && key === lastReport) return;
      lastReport = key;
      try { const r = await api('/v1/call/session/config', { method: 'POST', body: JSON.stringify({ config: state, seen: lastRev }) }); if (r?.rev) lastRev = Math.max(lastRev, r.rev); } catch (_) {}
    }, 300);
  }
  document.addEventListener('pocket:remote-rendered', () => {
    redrawing = false;
    reportSoon(true);
    if (nudgeWaiting && Date.now() - nudgeWaiting < 30000) nudgeBuyNow();
  });
  if (bar && window.MutationObserver) new MutationObserver(() => {
    reportSoon(false);
    autoSession();
    if (nudgeWaiting && Date.now() - nudgeWaiting < 30000 && bar.dataset.state === 'ready' && !bar.dataset.hold) nudgeBuyNow();
  }).observe(bar, { attributes: true, attributeFilter: ['data-state', 'data-hold'] });

  /** One state from the Worker (a socket frame or a poll): the call's status, its card changes and its nudges. */
  function handle(state) {
    if (!state || typeof state !== 'object' || !session) return;
    const was = onCall;
    onCall = !!state.onCall;
    // "On a call · changes show here" only where the card is (other pages: the chat and the pill's dot say so).
    if (pill) pill.hidden = !(onCall && bar);
    document.dispatchEvent(new CustomEvent('pocket:call-state', { detail: { onCall, linked: !!state.linked } }));
    callmeFollow(state);
    let changed = null;
    // The chat's own edit is applied by its reply (with its note), not by the push that may arrive first.
    const remote = ['call', 'chat', 'email'].includes(state.source) && !(state.source === 'chat' && chatHeld);
    if (state.config && Number(state.rev) > lastRev && remote) {
      changed = apply(state.config, state.source);
      for (const field of changed) capture('pf_call_edit', { field, source: state.source });
      if (changed.length) document.dispatchEvent(new CustomEvent('pocket:remote-applied', { detail: { fields: changed, source: state.source, card: state.config } }));
    }
    lastRev = Math.max(lastRev, Number(state.rev) || 0);
    // An edit with nothing to redraw (the page already shows it): this revision is drawn now.
    if (changed && !changed.length) reportSoon(true);
    if (state.nudge > lastNudge) { if (lastNudge || onCall) nudgeBuyNow(); lastNudge = state.nudge; }
    if (state.ended && !onCall) { session.until = Math.min(session.until, Date.now() + 10 * 60 * 1000); keep(); }
    if (was !== onCall) schedule();
  }

  let lastPolled = 0;
  async function poll() {
    if (!session) return;
    if (session.until < Date.now() || document.visibilityState === 'hidden') return;
    lastPolled = Date.now();
    let state;
    try { state = await api(`/v1/call/session/state?since=${lastRev}`); }
    catch (error) { if (error.status === 404) stop(); return; }
    handle(state);
  }

  // The live socket: the card as the assistant leaves it, pushed the moment it changes. A socket that never opens (an
  // older Worker without it) is tried a few times, then polling does the job for this page.
  let socket = null, socketOpen = false, socketTries = 0, socketEver = false, socketOff = false, socketTimer = 0, pinger = 0;
  function connect() {
    clearTimeout(socketTimer);
    if (!session || socket || socketOff || typeof window.WebSocket !== 'function' || document.visibilityState === 'hidden' || session.until < Date.now()) return;
    const base = worker();
    if (!base) return;
    let ws;
    try { ws = new window.WebSocket(base.replace(/^http/i, 'ws') + '/v1/call/session/live', ['pf-live.v1', session.token]); } catch (_) { retry(); return; }
    socket = ws;
    ws.addEventListener('open', () => {
      if (socket !== ws) return;
      socketOpen = true; socketEver = true; socketTries = 0;
      clearInterval(pinger); pinger = setInterval(() => { try { ws.send('ping'); } catch (_) {} }, PING_MS);
      schedule();
    });
    ws.addEventListener('message', event => {
      if (socket !== ws || typeof event.data !== 'string' || event.data === 'pong') return;
      let state = null;
      try { state = JSON.parse(event.data); } catch (_) { return; }
      if (state?.type === 'state') handle(state);
    });
    ws.addEventListener('close', event => {
      if (socket !== ws) return;
      socket = null; socketOpen = false; clearInterval(pinger);
      // 4401: the session is gone (expired or unknown); polling hears the same and stops.
      if (event.code === 4401) socketOff = true; else retry();
      poll(); schedule();
    });
  }
  function retry() {
    if (!socketEver && socketTries >= BACKOFF_MS.length) { socketOff = true; return; }
    clearTimeout(socketTimer);
    socketTimer = setTimeout(connect, BACKOFF_MS[Math.min(socketTries, BACKOFF_MS.length - 1)]);
    socketTries += 1;
  }
  function disconnect() { clearTimeout(socketTimer); clearInterval(pinger); const ws = socket; socket = null; socketOpen = false; try { ws?.close(1000); } catch (_) {} }

  // Polling: only while the socket is down (a keep-alive every few minutes otherwise, which also keeps the call's link to
  // this card fresh), fast while something is happening, never while the tab is hidden.
  let pollTimer = 0;
  const pace = () => (socketOpen ? KEEPALIVE_MS : onCall || chatOpen || ringing ? FAST_MS : SLOW_MS);
  function schedule() {
    clearTimeout(pollTimer); pollTimer = 0;
    if (!session || session.until < Date.now() || document.visibilityState === 'hidden') return;
    pollTimer = setTimeout(async () => { pollTimer = 0; await poll(); schedule(); }, Math.max(50, pace() - (Date.now() - lastPolled)));
  }
  /** Something happened (a chat, a tap, a change): the page keeps in sync for another 30 minutes. */
  function touch() {
    if (!session) return;
    const idle = session.until < Date.now();
    session.until = Math.max(session.until, Date.now() + WATCH_MS); keep();
    if (idle) { poll(); schedule(); }
    connect();
  }
  function watch() { connect(); poll(); schedule(); }
  /** The session is gone (a day old, or unknown): no sync until a new one is made. */
  function stop() { disconnect(); clearTimeout(pollTimer); pollTimer = 0; session = null; socketOff = false; socketTries = 0; socketEver = false; keep(); if (pill) pill.hidden = true; }
  document.addEventListener('visibilitychange', () => {
    if (!session) return;
    if (document.visibilityState === 'hidden') { clearTimeout(pollTimer); pollTimer = 0; return; }
    touch(); poll(); schedule();
  });
  let seenAt = 0;
  for (const type of ['pointerdown', 'keydown', 'scroll']) window.addEventListener(type, () => { if (session && Date.now() - seenAt > 30000) { seenAt = Date.now(); touch(); } }, { passive: true, capture: true });
  // The chat panel open or closed, a message on its way (assets/pocket-textcall.js).
  document.addEventListener('pocket:textcall', event => {
    if ('open' in (event.detail || {})) { chatOpen = !!event.detail.open; if (chatOpen) touch(); schedule(); }
    if ('busy' in (event.detail || {})) chatHeld = !!event.detail.busy;
  });

  /** The shopper's own changes during a call go back to the Worker (the assistant reads them with get_config). */
  function pushSoon(event) {
    if (!session || applying || !event.isTrusted) return;
    if (!event.target.closest?.('[data-card-field],[data-pack],[data-destination-input],[data-rv-review-link]') && !window.PocketPurpose.is(event.target)) return;
    touch();
    clearTimeout(pushTimer);
    pushTimer = setTimeout(async () => {
      try { const r = await api('/v1/call/session/config', { method: 'POST', body: JSON.stringify({ config: snapshot(), seen: lastRev }) }); if (r?.rev) lastRev = Math.max(lastRev, r.rev); } catch (_) {}
    }, 600);
  }
  document.addEventListener('change', pushSoon);
  document.addEventListener('input', pushSoon);

  /** The card session is made quietly as soon as the page shows a card preview (once per tab), so a call dialed by
   * hand can find this card by its business name. A refusal (a limit, the Worker down) just means no session yet: the
   * next chat message or Call makes one. */
  let autoTimer = 0;
  function autoSession() {
    if (session || making || !bar) return;
    try { if (sessionStorage.getItem(AUTO_KEY)) return; } catch (_) { return; }
    clearTimeout(autoTimer);
    autoTimer = setTimeout(() => {
      if (session || making) return;
      const card = snapshot();
      if (!(card.businessName || card.logoAsset) || bar.dataset.hold === 'preview' || bar.dataset.state === 'saving') return;
      try { sessionStorage.setItem(AUTO_KEY, '1'); } catch (_) {}
      ensureSession().catch(() => {});
    }, 1500);
  }

  /** Checkout started during a call (Buy now, quick pay or Buy): recorded once for the call log. */
  function checkoutStarted() { mark('checkout_started'); }
  document.addEventListener('pocket:buy-now-checkout', checkoutStarted);
  bar?.querySelector('[data-pay-area]')?.addEventListener('click', checkoutStarted, true);
  form.addEventListener?.('submit', checkoutStarted);

  // "Call me now": a view in the Text / Call panel (assets/pocket-textcall.js shows it and goes back to the chat).
  const callme = view && {
    form: view.querySelector('[data-pf-callme-form]'),
    phone: view.querySelector('[data-pf-callme-phone]'),
    consent: view.querySelector('[data-pf-callme-consent]'),
    error: view.querySelector('[data-pf-callme-error]'),
    status: view.querySelector('[data-pf-callme-status]'),
    later: view.querySelector('[data-pf-callme-later]'),
    laterText: view.querySelector('[data-pf-callme-later-text]'),
    schedule: view.querySelector('[data-pf-callme-schedule]'),
    submit: view.querySelector('[data-pf-callme-submit]'),
  };
  let lastCallme = '';
  const show = (node, text) => { if (!node) return; node.textContent = text || ''; node.hidden = !text; };
  /** The evening offer replaces Call me now (one main action at a time); a new number, or the view opened again,
   * brings Call me now back. */
  function laterOff() { if (callme?.later) callme.later.hidden = true; if (callme?.submit) callme.submit.hidden = false; }
  function openCallme(from) {
    if (!view) return;
    show(callme.error, ''); laterOff();
    window.PocketTextCall?.callme?.(from);
  }
  function closeCallme() { window.PocketTextCall?.chat?.(); }

  async function requestCall(scheduleIt) {
    show(callme.error, '');
    if (!callme.consent?.checked) { show(callme.error, 'Tick the box to agree to the call first.'); callme.consent?.focus(); return; }
    const digits = String(callme.phone?.value || '').replace(/\D/g, '');
    if (digits.length < 10) { show(callme.error, 'Enter a full U.S. phone number, like (555) 123-4567.'); callme.phone?.focus(); return; }
    const card = bar ? snapshot() : {};
    capture('pf_callme_request', { pack: card.pack || null, finish: card.finish || null, price: card.pack && card.finish ? priceOf(card.pack, card.finish) : null, device: device() });
    callme.submit.disabled = true; if (callme.schedule) callme.schedule.disabled = true;
    callme.submit.setAttribute('aria-busy', 'true');
    show(callme.status, scheduleIt ? 'Scheduling your call…' : 'Calling you now…');
    let sid = '';
    try { sid = window.PocketCore?.sessionId?.() || ''; } catch (_) {}
    let response, body = null;
    try {
      const base = worker();
      if (!base) throw new Error('no worker');
      response = await fetch(base + '/v1/call/outbound', { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(sid ? { 'X-PF-Session': sid } : {}) }, body: JSON.stringify({ phone: callme.phone.value, consent: true, consentVersion: view.dataset.consentVersion, config: card, page: location.href, sessionId: sid, schedule: !!scheduleIt }), signal: AbortSignal.timeout?.(12000) });
      body = await response.json().catch(() => null);
    } catch (_) { response = null; }
    callme.submit.disabled = false; if (callme.schedule) callme.schedule.disabled = false;
    callme.submit.removeAttribute('aria-busy');
    if (response?.status === 409 && body?.code === 'quiet_hours') {
      show(callme.status, '');
      if (callme.laterText) callme.laterText.textContent = body.callAtLabel ? `It's late where you are, so we won't call right now. We can call you at ${body.callAtLabel}.` : "It's late where you are, so we won't call right now.";
      if (callme.schedule) { callme.schedule.textContent = body.callAtLabel ? `Call me at ${body.callAtLabel}` : 'Call me in the morning'; callme.schedule.hidden = !body.callAt; }
      if (callme.later) {
        const focused = document.activeElement === callme.submit;
        callme.later.hidden = false; callme.submit.hidden = !!body.callAt;
        // The button they pressed is gone: focus moves to the one that replaces it (its description says why).
        if (callme.submit.hidden && focused) callme.schedule?.focus({ preventScroll: true });
      }
      return;
    }
    if (!response || !response.ok || !body?.token) {
      show(callme.status, '');
      show(callme.error, body?.error || `We couldn't place the call. Call us free at ${DISPLAY}.`);
      return;
    }
    disconnect();
    session = { token: body.token, until: Date.now() + WATCH_MS, dial: dialOf(body.dial) };
    lastRev = 1; lastNudge = 0; socketOff = false; socketTries = 0; socketEver = false; keep();
    if (callme.later) callme.later.hidden = true;
    if (body.status === 'scheduled') {
      show(callme.status, `Scheduled: we'll call you shortly after ${body.callAtLabel}. You can close this page.`);
      return;
    }
    show(callme.status, 'Calling you now…');
    lastCallme = 'calling'; ringing = true;
    watch();
  }
  callme?.form?.addEventListener('submit', event => { event.preventDefault(); requestCall(false); });
  callme?.schedule?.addEventListener('click', () => requestCall(true));
  callme?.phone?.addEventListener('input', () => { if (callme.later && !callme.later.hidden) laterOff(); });

  /** The callback's progress, from each push or poll: ringing, answered (the pill takes over), or not reached. */
  function callmeFollow(state) {
    const now = state.callme?.status || '';
    if (!now || now === lastCallme) return;
    lastCallme = now;
    ringing = now === 'calling';
    if (now === 'connected') { show(callme?.status, ''); closeCallme(); }
    else if (now === 'noanswer' || now === 'voicemail' || now === 'failed') show(callme?.status, `We couldn't reach you. Tap Call me now to try again, or call us free at ${DISPLAY}.`);
  }

  if (session) watch();
  else if (bar) autoSession();
  window.PocketCall = { snapshot, apply, applyFromChat, poll, openCallme, readiness, ensureSession, callAgent, api, at, nudge: nudgeBuyNow, seen: () => lastRev, session: () => session, hasCard: () => !!bar, reset: stop, live: () => socketOpen };
})();
